import { GetStaticPaths, GetStaticProps } from 'next';

import { EfroOostInformationFieldsFragment, EfroOostPageSlugFieldsFragment } from 'api/graphql-types';
import { getServerPageGetEfroOost, getServerPageGetEfroOostSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import { BaseModule } from 'modules/base-module/base-module';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { logger } from 'utils/logger';

interface ICatchAllPageProps extends IBasePageProps {
	page?: EfroOostPageSlugFieldsFragment;
	pageInformation?: EfroOostInformationFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout: any | null;
}

const CatchAllPage = (props: ICatchAllPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.page || null,
		pageQuery: () => getServerPageGetEfroOost({ variables: props.variables }, client)
	});

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={props?.page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'EfroOostPage' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page.header?.backgroundColor || 'white',
							padding: 'none'
						}}
					>
						<IntroHeader module={page.header as IModule} slug={page.slug} />
					</BaseModule>
					<ModuleBuilder modules={page.modules || []} slug={page.slug} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	try {
		const pageSlugsResult = await getServerPageGetEfroOostSlugs({}, client);
		const slugResult = pageSlugsResult.props.data?.findAllSlugs;

		const paths = slugResult?.map((slugObject) => ({
			params: {
				slug: slugObject?.slug?.split('/').filter((slug) => slug !== '') || []
			},
			locale: slugObject?.locale || ctx.defaultLocale
		}));

		return {
			paths: paths || [],
			fallback: 'blocking'
		};
	} catch (error) {
		logger.error('Failed to generate static paths for EFRO Oost pages', error as Error, {
			component: 'getStaticPaths',
			site: 'efro-oost'
		});

		return {
			paths: [],
			fallback: 'blocking'
		};
	}
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const slug = ctx.params?.slug as string[];
		const fullSlug = `/${slug.join('/')}`;
		const variables = { slug: fullSlug, locale, publicationState };

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::efro-oost-page.efro-oost-page',
			contentTypeName: 'efro-oost-page',
			neutralStyle: true,
			hideLanguageSwitcher: true,
			hideSearchbar: false,
			isSubsite: true,
			fetchPage: () => getServerPageGetEfroOost({ variables }, client),
			extractInformation: (data) => data?.props?.data?.efroOostInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.efroOostPageBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				page: passwordResult.page,
				pageInformation: passwordResult.pageInformation,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error) {
		const slug = ctx.params?.slug as string[];
		const fullSlug = slug ? `/${slug.join('/')}` : 'unknown';
		const { locale } = getStrapiParametersFromContext(ctx);

		logger.staticPropsError('Failed to generate static props for EFRO Oost page', error as Error, fullSlug, locale, {
			params: ctx.params,
			preview: ctx.preview,
			site: 'efro-oost'
		});

		return { notFound: true };
	}
};

export default CatchAllPage;
